/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { DBService } from './DBService';
import type { AppSettings } from '../types/types';

const CUSTOM_MODEL_OPTION = '__custom__';
const DEFAULT_MODEL_OPTIONS = [
  'gemini-flash-latest',
  'gemini-pro-latest',
  'gemini-flash-lite-latest',
  'gemini-2.5-flash',
  'gemini-2.5-flash-lite',
  'gemini-2.5-pro',
  'gemini-2.0-flash',
];
const VOICE_TRANSCRIPTION_MODEL_OPTIONS = ['gemini-pro-latest', 'gemini-flash-latest', 'gemini-flash-lite-latest'];
const FAST_MODEL_OPTIONS = ['gemini-flash-latest', 'gemini-flash-lite-latest'];
const TITLE_GENERATOR_MODEL_OPTIONS = ['gemini-flash-latest', 'gemini-flash-lite-latest'];
const REWRITE_POLISHING_MODEL_OPTIONS = ['gemini-pro-latest', 'gemini-flash-latest'];

/**
 * Manages application settings and persistence to IndexedDB.
 */
export class SettingsService {
  private statusManager: StatusManager;
  
  // Service registry getters
  private get dom() { return ServiceRegistry.get<DomController>('dom'); }
  private get db() { return ServiceRegistry.get<DBService>('dbService'); }

  public isPolishingEnabled = true;
  public isTranslationEnabled = false;
  public translatePolished = true;
  public translateRaw = false;
  public targetLanguage = 'English';
  public outputLanguage: 'autodetect' | 'English' | 'Serbian' = 'autodetect';
  public currentModel: string = process.env.MODEL_NAME || 'gemini-flash-latest';
  public voiceTranscriptionModel = 'gemini-flash-latest';
  public fastModel = 'gemini-flash-latest';
  public titleGeneratorModel = 'gemini-flash-lite-latest';
  public rewritePolishingModel = 'gemini-pro-latest';
  public isToolbarSticky = false;
  public currentTheme: 'light' | 'dark' = 'dark';
  public isAutosaveEnabled = false;
  public autosaveFrequency = 30000; // Default to 30 seconds
  public recordingMode: 'transcription' | 'voice_note' = 'transcription';
  public sourceView = false;
  
  constructor() {
    this.statusManager = new StatusManager(this.dom);
  }
  
  public async initSettings(): Promise<void> {
    const dbRecordingMode = await this.db.getSetting('recordingMode') as 'transcription' | 'voice_note' | null | undefined;
    const defaultRecordingMode: 'transcription' | 'voice_note' = 'transcription';
    
    const settings: AppSettings = {
        geminiModel: await this.db.getSetting('geminiModel') ?? this.currentModel,
        voiceTranscriptionModel: await this.db.getSetting('voiceTranscriptionModel') ?? this.voiceTranscriptionModel,
        fastModel: await this.db.getSetting('fastModel') ?? this.fastModel,
        titleGeneratorModel: await this.db.getSetting('titleGeneratorModel') ?? this.titleGeneratorModel,
        rewritePolishingModel: await this.db.getSetting('rewritePolishingModel') ?? this.rewritePolishingModel,
        theme: await this.db.getSetting('theme') ?? this.currentTheme,
        polishingEnabled: await this.db.getSetting('polishingEnabled') ?? this.isPolishingEnabled,
        translationEnabled: await this.db.getSetting('translationEnabled') ?? this.isTranslationEnabled,
        translatePolished: await this.db.getSetting('translatePolished') ?? this.translatePolished,
        translateRaw: await this.db.getSetting('translateRaw') ?? this.translateRaw,
        targetLanguage: await this.db.getSetting('targetLanguage') ?? this.targetLanguage,
        outputLanguage: await this.db.getSetting('outputLanguage') ?? this.outputLanguage,
        toolbarSticky: await this.db.getSetting('toolbarSticky') ?? this.isToolbarSticky,
        isAutosaveEnabled: await this.db.getSetting('isAutosaveEnabled') ?? this.isAutosaveEnabled,
        autosaveFrequency: await this.db.getSetting('autosaveFrequency') ?? this.autosaveFrequency,
        recordingMode: (dbRecordingMode !== null && dbRecordingMode !== undefined) ? dbRecordingMode : defaultRecordingMode,
        sourceView: await this.db.getSetting('sourceView') ?? this.sourceView,
    };
    this.applySettings(settings, false);
  }
  
  public async applySettings(settings: AppSettings, saveToDb = true): Promise<void> {
    this.currentModel = settings.geminiModel;
    this.voiceTranscriptionModel = settings.voiceTranscriptionModel ?? this.voiceTranscriptionModel;
    this.fastModel = settings.fastModel ?? this.fastModel;
    this.titleGeneratorModel = settings.titleGeneratorModel ?? this.titleGeneratorModel;
    this.rewritePolishingModel = settings.rewritePolishingModel ?? this.rewritePolishingModel;
    this.currentTheme = settings.theme;
    this.isPolishingEnabled = settings.polishingEnabled;
    this.isTranslationEnabled = settings.translationEnabled;
    this.translatePolished = settings.translatePolished;
    this.translateRaw = settings.translateRaw;
    this.targetLanguage = settings.targetLanguage;
    this.outputLanguage = settings.outputLanguage;
    this.isToolbarSticky = settings.toolbarSticky;
    this.isAutosaveEnabled = settings.isAutosaveEnabled;
    this.autosaveFrequency = settings.autosaveFrequency;
    this.recordingMode = settings.recordingMode;
    this.sourceView = settings.sourceView;

    if (saveToDb) {
        await Promise.all([
            this.db.setSetting('geminiModel', this.currentModel),
            this.db.setSetting('voiceTranscriptionModel', this.voiceTranscriptionModel),
            this.db.setSetting('fastModel', this.fastModel),
            this.db.setSetting('titleGeneratorModel', this.titleGeneratorModel),
            this.db.setSetting('rewritePolishingModel', this.rewritePolishingModel),
            this.db.setSetting('theme', this.currentTheme),
            this.db.setSetting('polishingEnabled', this.isPolishingEnabled),
            this.db.setSetting('translationEnabled', this.isTranslationEnabled),
            this.db.setSetting('translatePolished', this.translatePolished),
            this.db.setSetting('translateRaw', this.translateRaw),
            this.db.setSetting('targetLanguage', this.targetLanguage),
            this.db.setSetting('outputLanguage', this.outputLanguage),
            this.db.setSetting('toolbarSticky', this.isToolbarSticky),
            this.db.setSetting('isAutosaveEnabled', this.isAutosaveEnabled),
            this.db.setSetting('autosaveFrequency', this.autosaveFrequency),
            this.db.setSetting('recordingMode', this.recordingMode),
            this.db.setSetting('sourceView', this.sourceView),
        ]);
    }
    this.updateDomFromState();
  }
  
  private updateDomFromState(): void {
    this.syncModelControls();
    this.dom.polishToggle.checked = this.isPolishingEnabled;
    this.dom.updatePolishingButtonVisuals(this.isPolishingEnabled);
    this.dom.translateToggle.checked = this.isTranslationEnabled;
    this.dom.translatePolishedToggle.checked = this.translatePolished;
    this.dom.translateRawToggle.checked = this.translateRaw;
    this.dom.translateLanguageSelect.value = this.targetLanguage;
    this.dom.outputLanguageSelect.value = this.outputLanguage;
    this.dom.updateTranslationButtonVisuals(this.isTranslationEnabled, this.targetLanguage);
    this.updateTranslateNoteButtonState();
    this.dom.autosaveToggle.checked = this.isAutosaveEnabled;
    this.dom.autosaveFrequency.value = String(this.autosaveFrequency);
    this.dom.autosaveFrequencyOption.classList.toggle('hidden', !this.isAutosaveEnabled);
    this.dom.recordingModeToggle.checked = this.recordingMode === 'voice_note';
    this.dom.sourceViewToggle.checked = this.sourceView;
    this.dom.initTheme(this.currentTheme);
  }
  
  public getAllSettings(): AppSettings {
      return {
          geminiModel: this.currentModel,
          voiceTranscriptionModel: this.voiceTranscriptionModel,
          fastModel: this.fastModel,
          titleGeneratorModel: this.titleGeneratorModel,
          rewritePolishingModel: this.rewritePolishingModel,
          theme: this.currentTheme,
          polishingEnabled: this.isPolishingEnabled,
          translationEnabled: this.isTranslationEnabled,
          translatePolished: this.translatePolished,
          translateRaw: this.translateRaw,
          targetLanguage: this.targetLanguage,
          outputLanguage: this.outputLanguage,
          toolbarSticky: this.isToolbarSticky,
          isAutosaveEnabled: this.isAutosaveEnabled,
          autosaveFrequency: this.autosaveFrequency,
          recordingMode: this.recordingMode,
          sourceView: this.sourceView,
      };
  }

  public async updateModelSelection(): Promise<void> {
    const selectedOption = this.dom.modelSelect.value;
    this.currentModel = this.resolveModelFromControl(selectedOption, this.dom.modelCustomInput.value, this.currentModel);
    await this.db.setSetting('geminiModel', this.currentModel);
    this.syncModelControl(
      this.dom.modelSelect,
      this.dom.modelCustomInputOption,
      this.dom.modelCustomInput,
      this.currentModel,
      DEFAULT_MODEL_OPTIONS,
      selectedOption === CUSTOM_MODEL_OPTION,
    );
  }

  public async updateOutputLanguage(): Promise<void> {
    this.outputLanguage = this.dom.outputLanguageSelect.value as 'autodetect' | 'English' | 'Serbian';
    await this.db.setSetting('outputLanguage', this.outputLanguage);
  }

  public async updateVoiceTranscriptionModelSelection(): Promise<void> {
    const selectedOption = this.dom.voiceTranscriptionModelSelect.value;
    this.voiceTranscriptionModel = this.resolveModelFromControl(
      selectedOption,
      this.dom.voiceTranscriptionModelCustomInput.value,
      this.voiceTranscriptionModel,
    );
    await this.db.setSetting('voiceTranscriptionModel', this.voiceTranscriptionModel);
    this.syncModelControl(
      this.dom.voiceTranscriptionModelSelect,
      this.dom.voiceTranscriptionModelCustomInputOption,
      this.dom.voiceTranscriptionModelCustomInput,
      this.voiceTranscriptionModel,
      VOICE_TRANSCRIPTION_MODEL_OPTIONS,
      selectedOption === CUSTOM_MODEL_OPTION,
    );
  }

  public async updateFastModelSelection(): Promise<void> {
    const selectedOption = this.dom.fastModelSelect.value;
    this.fastModel = this.resolveModelFromControl(selectedOption, this.dom.fastModelCustomInput.value, this.fastModel);
    await this.db.setSetting('fastModel', this.fastModel);
    this.syncModelControl(
      this.dom.fastModelSelect,
      this.dom.fastModelCustomInputOption,
      this.dom.fastModelCustomInput,
      this.fastModel,
      FAST_MODEL_OPTIONS,
      selectedOption === CUSTOM_MODEL_OPTION,
    );
  }

  public async updateTitleGeneratorModelSelection(): Promise<void> {
    const selectedOption = this.dom.titleGeneratorModelSelect.value;
    this.titleGeneratorModel = this.resolveModelFromControl(
      selectedOption,
      this.dom.titleGeneratorModelCustomInput.value,
      this.titleGeneratorModel,
    );
    await this.db.setSetting('titleGeneratorModel', this.titleGeneratorModel);
    this.syncModelControl(
      this.dom.titleGeneratorModelSelect,
      this.dom.titleGeneratorModelCustomInputOption,
      this.dom.titleGeneratorModelCustomInput,
      this.titleGeneratorModel,
      TITLE_GENERATOR_MODEL_OPTIONS,
      selectedOption === CUSTOM_MODEL_OPTION,
    );
  }

  public async updateRewritePolishingModelSelection(): Promise<void> {
    const selectedOption = this.dom.rewritePolishingModelSelect.value;
    this.rewritePolishingModel = this.resolveModelFromControl(
      selectedOption,
      this.dom.rewritePolishingModelCustomInput.value,
      this.rewritePolishingModel,
    );
    await this.db.setSetting('rewritePolishingModel', this.rewritePolishingModel);
    this.syncModelControl(
      this.dom.rewritePolishingModelSelect,
      this.dom.rewritePolishingModelCustomInputOption,
      this.dom.rewritePolishingModelCustomInput,
      this.rewritePolishingModel,
      REWRITE_POLISHING_MODEL_OPTIONS,
      selectedOption === CUSTOM_MODEL_OPTION,
    );
  }

  public getDefaultModel(): string {
    return this.currentModel;
  }

  public getVoiceTranscriptionModel(): string {
    return this.voiceTranscriptionModel;
  }

  public getFastModel(): string {
    return this.fastModel;
  }

  public getTitleGeneratorModel(): string {
    return this.titleGeneratorModel;
  }

  public getRewritePolishingModel(): string {
    return this.rewritePolishingModel;
  }

  private syncModelControls(): void {
    this.syncModelControl(this.dom.modelSelect, this.dom.modelCustomInputOption, this.dom.modelCustomInput, this.currentModel, DEFAULT_MODEL_OPTIONS);
    this.syncModelControl(
      this.dom.voiceTranscriptionModelSelect,
      this.dom.voiceTranscriptionModelCustomInputOption,
      this.dom.voiceTranscriptionModelCustomInput,
      this.voiceTranscriptionModel,
      VOICE_TRANSCRIPTION_MODEL_OPTIONS,
    );
    this.syncModelControl(this.dom.fastModelSelect, this.dom.fastModelCustomInputOption, this.dom.fastModelCustomInput, this.fastModel, FAST_MODEL_OPTIONS);
    this.syncModelControl(
      this.dom.titleGeneratorModelSelect,
      this.dom.titleGeneratorModelCustomInputOption,
      this.dom.titleGeneratorModelCustomInput,
      this.titleGeneratorModel,
      TITLE_GENERATOR_MODEL_OPTIONS,
    );
    this.syncModelControl(
      this.dom.rewritePolishingModelSelect,
      this.dom.rewritePolishingModelCustomInputOption,
      this.dom.rewritePolishingModelCustomInput,
      this.rewritePolishingModel,
      REWRITE_POLISHING_MODEL_OPTIONS,
    );
  }

  private syncModelControl(
    select: HTMLSelectElement,
    customInputOption: HTMLDivElement,
    customInput: HTMLInputElement,
    selectedModel: string,
    predefinedOptions: string[],
    forceCustomInput = false,
  ): void {
    if (forceCustomInput) {
      select.value = CUSTOM_MODEL_OPTION;
      if (!customInput.value.trim() && !predefinedOptions.includes(selectedModel)) {
        customInput.value = selectedModel;
      }
      customInputOption.classList.remove('hidden');
      return;
    }

    if (predefinedOptions.includes(selectedModel)) {
      select.value = selectedModel;
      customInputOption.classList.add('hidden');
      return;
    }

    select.value = CUSTOM_MODEL_OPTION;
    customInput.value = selectedModel;
    customInputOption.classList.remove('hidden');
  }

  private resolveModelFromControl(selection: string, customModelInput: string, fallback: string): string {
    if (selection !== CUSTOM_MODEL_OPTION) {
      return selection;
    }

    const customModel = customModelInput.trim();
    return customModel || fallback;
  }

  public async updatePolishingState(): Promise<void> {
    this.isPolishingEnabled = this.dom.polishToggle.checked;
    await this.db.setSetting('polishingEnabled', this.isPolishingEnabled);
    this.dom.updatePolishingButtonVisuals(this.isPolishingEnabled);
  }

  public async updateTranslationState(): Promise<void> {
    this.isTranslationEnabled = this.dom.translateToggle.checked;
    this.translatePolished = this.dom.translatePolishedToggle.checked;
    this.translateRaw = this.dom.translateRawToggle.checked;
    this.targetLanguage = this.dom.translateLanguageSelect.value;
    await this.db.setSetting('translationEnabled', this.isTranslationEnabled);
    await this.db.setSetting('translatePolished', this.translatePolished);
    await this.db.setSetting('translateRaw', this.translateRaw);
    await this.db.setSetting('targetLanguage', this.targetLanguage);
    this.dom.updateTranslationButtonVisuals(this.isTranslationEnabled, this.targetLanguage);
    this.updateTranslateNoteButtonState();
  }
  
  public updateTranslateNoteButtonState(): void {
      this.dom.manualTranslateButton.disabled = !(this.translatePolished || this.translateRaw);
  }

  public async updateToolbarStickyState(isSticky: boolean): Promise<void> {
    this.isToolbarSticky = isSticky;
    await this.db.setSetting('toolbarSticky', this.isToolbarSticky);
  }

  public async getTheme(): Promise<'light' | 'dark'> {
    return await this.db.getSetting('theme') ?? 'dark';
  }

  public async setTheme(theme: 'light' | 'dark'): Promise<void> {
    this.currentTheme = theme;
    await this.db.setSetting('theme', theme);
  }

  public async updateAutosaveState(): Promise<void> {
    this.isAutosaveEnabled = this.dom.autosaveToggle.checked;
    this.autosaveFrequency = Number(this.dom.autosaveFrequency.value);
    await this.db.setSetting('isAutosaveEnabled', this.isAutosaveEnabled);
    await this.db.setSetting('autosaveFrequency', this.autosaveFrequency);
    this.dom.autosaveFrequencyOption.classList.toggle('hidden', !this.isAutosaveEnabled);
  }

  public async updateRecordingMode(): Promise<void> {
    this.recordingMode = this.dom.recordingModeToggle.checked ? 'voice_note' : 'transcription';
    await this.db.setSetting('recordingMode', this.recordingMode);
  }

  public async updateSourceView(enabled: boolean): Promise<void> {
    this.sourceView = enabled;
    await this.db.setSetting('sourceView', this.sourceView);
  }
}